import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { FileText, Plus, Loader2, Eye, Download, Archive, ArchiveRestore, Filter } from 'lucide-react';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';
import { toast } from 'sonner';
import { downloadParteTrabajoePdf, openParteTrabajoePdf } from '@/lib/parteTrabajoePdfUtils';
import { loadEmpresaYConfigParaPdf } from '@/lib/pdfBranding';

interface PartesClienteTabProps {
  clienteId: string;
  clienteNombre: string;
}

interface ParteTrabajo {
  id: string;
  numero_parte: string | null;
  fecha_trabajo: string;
  tipo_intervencion: string;
  estado: 'borrador' | 'finalizado' | 'facturado';
  resultado_intervencion: string | null;
  descripcion_trabajo: string;
  archivado: boolean;
  tecnico_id: string;
  empresa_id: string | null;
  cliente_id: string | null;
  cliente_libre: string | null;
  perfiles: { nombre: string } | null;
  [k: string]: any;
}

const tipoIntervencionLabels: Record<string, string> = {
  revision_sistema_seguridad: 'Revisión Sistema',
  averias: 'Averías',
  auditoria: 'Auditoría',
  entrega_materiales: 'Entrega Materiales',
  otros: 'Otros',
};

const tiposFiltro = ['', 'revision_sistema_seguridad', 'averias', 'auditoria', 'entrega_materiales', 'otros'] as const;

const estadoBadgeClass: Record<string, string> = {
  borrador: 'bg-orange-500/20 text-orange-700 dark:text-orange-400 border-orange-500/30',
  finalizado: 'bg-blue-500/20 text-blue-700 dark:text-blue-400 border-blue-500/30',
  facturado: 'bg-emerald-500/20 text-emerald-700 dark:text-emerald-400 border-emerald-500/30',
};

export const PartesClienteTab: React.FC<PartesClienteTabProps> = ({ clienteId, clienteNombre }) => {
  const navigate = useNavigate();
  const [partes, setPartes] = useState<ParteTrabajo[]>([]);
  const [loading, setLoading] = useState(true);
  const [mostrarArchivados, setMostrarArchivados] = useState(false);
  const [filtroTipo, setFiltroTipo] = useState<string>('');
  const [loadingPdf, setLoadingPdf] = useState<string | null>(null);
  const [clienteData, setClienteData] = useState<any>(null);

  const cargar = async () => {
    setLoading(true);
    const [{ data, error }, { data: cli }] = await Promise.all([
      supabase
        .from('partes_trabajo')
        .select('*, perfiles!fk_partes_trabajo_tecnico(nombre)')
        .eq('cliente_id', clienteId)
        .order('fecha_trabajo', { ascending: false }),
      supabase.from('clientes').select('*').eq('id', clienteId).maybeSingle(),
    ]);

    if (!error && data) setPartes(data as unknown as ParteTrabajo[]);
    setClienteData(cli || { nombre: clienteNombre });
    setLoading(false);
  };

  useEffect(() => { cargar(); }, [clienteId]);

  const handleDownloadPdf = async (parte: ParteTrabajo) => {
    setLoadingPdf(parte.id);
    try {
      const empresaId = parte.empresa_id;
      const { empresa, docConfig } = await loadEmpresaYConfigParaPdf(empresaId || undefined);
      const tecnicoNombre = parte.perfiles?.nombre || 'Técnico';
      await downloadParteTrabajoePdf(parte as any, clienteData?.nombre || clienteNombre, tecnicoNombre, clienteData, empresa, docConfig);
      toast.success('PDF descargado');
    } catch (e) {
      console.error('Error PDF', e);
      toast.error('No se pudo descargar el PDF');
    } finally {
      setLoadingPdf(null);
    }
  };

  const handleViewPdf = async (parte: ParteTrabajo) => {
    setLoadingPdf(parte.id);
    try {
      const empresaId = parte.empresa_id;
      const { empresa, docConfig } = await loadEmpresaYConfigParaPdf(empresaId || undefined);
      const tecnicoNombre = parte.perfiles?.nombre || 'Técnico';
      await openParteTrabajoePdf(parte as any, clienteData?.nombre || clienteNombre, tecnicoNombre, clienteData, empresa, docConfig);
    } catch (e) {
      console.error('Error PDF', e);
      toast.error('No se pudo abrir el PDF');
    } finally {
      setLoadingPdf(null);
    }
  };

  const handleEstadoChange = async (parteId: string, nuevo: 'borrador' | 'finalizado' | 'facturado') => {
    const { error } = await supabase.from('partes_trabajo').update({ estado: nuevo }).eq('id', parteId);
    if (error) { toast.error('No se pudo actualizar el estado'); return; }
    setPartes(prev => prev.map(p => p.id === parteId ? { ...p, estado: nuevo } : p));
    toast.success('Estado actualizado');
  };

  const handleToggleArchivar = async (parteId: string, archivar: boolean) => {
    const { error } = await supabase
      .from('partes_trabajo')
      .update({ archivado: archivar } as any)
      .eq('id', parteId);
    if (error) { toast.error('No se pudo archivar'); return; }
    setPartes(prev => prev.map(p => p.id === parteId ? { ...p, archivado: archivar } : p));
    toast.success(archivar ? 'Parte archivado' : 'Parte desarchivado');
  };

  const partesVisibles = partes.filter(p => {
    const matchArch = mostrarArchivados ? p.archivado : !p.archivado;
    if (!matchArch) return false;
    if (filtroTipo && p.tipo_intervencion !== filtroTipo) return false;
    return true;
  });

  if (loading) {
    return (
      <Card>
        <CardContent className="flex items-center justify-center py-12">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <CardTitle className="flex items-center gap-2">
            <FileText className="h-5 w-5" />
            Partes de Trabajo
            <Badge variant="secondary" className="ml-2">{partesVisibles.length}</Badge>
          </CardTitle>
          <div className="flex items-center gap-3 flex-wrap">
            <div className="flex items-center gap-1.5 flex-wrap">
              <Filter className="h-3.5 w-3.5 text-muted-foreground" />
              <span className="text-xs text-muted-foreground hidden sm:inline">Tipo:</span>
              {tiposFiltro.map((t) => (
                <Button
                  key={t || 'all'}
                  variant={filtroTipo === t ? 'default' : 'outline'}
                  size="sm"
                  className="h-7 text-xs px-2.5"
                  onClick={() => setFiltroTipo(t)}
                >
                  {t ? tipoIntervencionLabels[t] : 'Todos'}
                </Button>
              ))}
            </div>
            <div className="flex items-center gap-2">
              <Switch id="partes-archivados" checked={mostrarArchivados} onCheckedChange={setMostrarArchivados} />
              <Label htmlFor="partes-archivados" className="text-sm cursor-pointer">Ver archivados</Label>
            </div>
            <Button size="sm" onClick={() => navigate(`/partes-trabajo?cliente_id=${clienteId}`)}>
              <Plus className="h-4 w-4 mr-1" /> Crear Parte
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        {partesVisibles.length === 0 ? (
          <div className="text-center py-8">
            <FileText className="h-12 w-12 mx-auto text-muted-foreground/50 mb-3" />
            <p className="text-muted-foreground">
              {mostrarArchivados ? 'No hay partes archivados' : 'No hay partes de trabajo para este cliente'}
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Nº Parte</TableHead>
                  <TableHead>Fecha</TableHead>
                  <TableHead className="hidden sm:table-cell">Técnico</TableHead>
                  <TableHead>Tipo</TableHead>
                  <TableHead>Estado</TableHead>
                  <TableHead className="text-right">Acciones</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {partesVisibles.map((parte) => (
                  <TableRow key={parte.id} className={parte.archivado ? 'opacity-60' : ''}>
                    <TableCell className="font-medium">{parte.numero_parte || '—'}</TableCell>
                    <TableCell>{format(new Date(parte.fecha_trabajo), 'dd/MM/yyyy', { locale: es })}</TableCell>
                    <TableCell className="hidden sm:table-cell">{parte.perfiles?.nombre || '—'}</TableCell>
                    <TableCell>
                      <Badge variant="outline">
                        {tipoIntervencionLabels[parte.tipo_intervencion] || parte.tipo_intervencion}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <Select value={parte.estado} onValueChange={(v) => handleEstadoChange(parte.id, v as any)}>
                        <SelectTrigger className={`h-8 w-[130px] ${estadoBadgeClass[parte.estado] || ''}`}>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="borrador">Borrador</SelectItem>
                          <SelectItem value="finalizado">Finalizado</SelectItem>
                          <SelectItem value="facturado">Facturado</SelectItem>
                        </SelectContent>
                      </Select>
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex items-center justify-end gap-1">
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => handleViewPdf(parte)}
                          disabled={loadingPdf === parte.id}
                          title="Ver PDF"
                        >
                          <Eye className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => handleDownloadPdf(parte)}
                          disabled={loadingPdf === parte.id}
                          title="Descargar PDF"
                        >
                          {loadingPdf === parte.id ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => handleToggleArchivar(parte.id, !parte.archivado)}
                          title={parte.archivado ? 'Desarchivar' : 'Archivar'}
                        >
                          {parte.archivado ? <ArchiveRestore className="h-4 w-4" /> : <Archive className="h-4 w-4" />}
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </CardContent>
    </Card>
  );
};
